import { useQuery } from '@tanstack/react-query';
import { z, ZodError } from 'zod';

import { axiosClient } from '@/shared/apis/config';
import type { ApiResponse } from '@/shared/types';
import type { CustomQueryOptions } from '@/shared/types/global/react-query/query';
import { voicePresetSchema } from '@/entities/voice-preset/model/voice-preset';

/**
 * GET /v1.0/voice-presets — SYSTEM 시드 + 본인 USER 프리셋 통합 목록.
 *
 * FSD entities 레이어 단일 SoT. caller (features/my-voice / features/weather-caster /
 * 향후 신규 caller) 모두 본 hook 만 import. 이전에 features/my-voice 와
 * features/weather-caster 가 자체 hook 을 보유하던 cross-slice 답습을 단일화.
 *
 * 응답은 `DataResponse<{items, meta}>` — meta 는 quota 표시용 (Phase 1 결정 5).
 */
export const GetVoicePresetsResponseSchema = z.object({
  items: z.array(voicePresetSchema),
  meta: z.object({
    userCount: z.number(),
    userLimit: z.number(),
  }),
});

export type GetVoicePresetsResponse = z.infer<
  typeof GetVoicePresetsResponseSchema
>;

type Response = ApiResponse<GetVoicePresetsResponse>;

export type GetVoicePresetsParams = {
  /** SYSTEM 프리셋 포함 여부. 기본 true. */
  includeSystem?: boolean;
  /** Playground resource policy를 적용해야 하는 호출에서만 true. */
  playgroundOnly?: boolean;
};

export const getVoicePresetsApi = async (
  params?: GetVoicePresetsParams,
): Promise<Response> => {
  try {
    const response = await axiosClient.get<Response>('/v1.0/voice-presets', {
      params: {
        includeSystem: params?.includeSystem ?? true,
        ...(params?.playgroundOnly ? { playgroundOnly: true } : {}),
      },
    });

    GetVoicePresetsResponseSchema.parse(response.data.data);

    return response.data;
  } catch (error) {
    if (error instanceof ZodError) {
      console.error('voice-preset 응답 검증 실패', error.issues);
      return Promise.reject(
        new Error('서버 응답 데이터 형식이 올바르지 않습니다.'),
      );
    }

    return Promise.reject(error);
  }
};

/**
 * voice-preset 목록 query key (entities 레이어 단일 SoT).
 *
 * 모든 caller (my-voice + weather-caster + 향후 신규) 가 본 key 로 invalidate.
 * mutation onSuccess 에서 본 key invalidate 시 모든 caller 동기 갱신.
 */
export const QueryKeyVoicePresets = ['voice-presets'] as const;

/**
 * voice-preset 은 사용자가 my-voice 에서 추가/삭제 가능한 동적 자산이라 staleTime 미설정
 * (default 0). 모든 caller (my-voice quota 표시 / weather-caster Step2 picker / 등) 가
 * 동일 hook 사용. mutation onSuccess 의 invalidateQueries 에 [QueryKeyVoicePresets] 만
 * 포함하면 모든 caller 자동 동기화.
 */
export const useGetVoicePresetsApi = (
  params?: GetVoicePresetsParams,
  options?: CustomQueryOptions<Response>,
) => {
  const { queryKey, ...rest } = options ?? {};

  return useQuery({
    queryKey: [
      ...QueryKeyVoicePresets,
      params?.includeSystem ?? true,
      params?.playgroundOnly ?? false,
      ...(queryKey ?? []),
    ],
    queryFn: () => getVoicePresetsApi(params),
    ...rest,
  });
};
